import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  fetchHarvestTypes,
  createHarvestType,
  updateHarvestType,
  deleteHarvestType,
} from "../api/harvestTypeApi";
import "../styles/Simplemanage.css";

const ManageHarvestTypes = () => {
  const [harvestTypes, setHarvestTypes] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ name: "" });

  // ── Load ──
  const loadHarvestTypes = async () => {
    try {
      const data = await fetchHarvestTypes();
      setHarvestTypes(data);
    } catch {
      Swal.fire("Error", "Failed to load harvest types", "error");
    }
  };

  useEffect(() => { loadHarvestTypes(); }, []);

  // ── Form ──
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setEditingItem(null);
    setForm({ name: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return Swal.fire("Required", "Harvest type name is required", "error");
    try {
      if (editingItem) {
        await updateHarvestType(editingItem.id, { name: form.name.trim() });
        Swal.fire("Updated!", "Harvest type updated successfully", "success");
      } else {
        await createHarvestType({ name: form.name.trim() });
        Swal.fire("Created!", "Harvest type added successfully", "success");
      }
      loadHarvestTypes();
      resetForm();
    } catch {
      Swal.fire("Error", "Operation failed", "error");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({ name: item.name });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this harvest type?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a8a49a",
      confirmButtonText: "Yes, delete it",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteHarvestType(id);
      loadHarvestTypes();
      Swal.fire("Deleted!", "Record removed successfully", "success");
    } catch {
      Swal.fire("Error", "Failed to delete record", "error");
    }
  };

  return (
    <div className="sm-root">

      {/* ── Header ── */}
      <div className="sm-header">
        <h1 className="sm-header-title">
          Manage <span>Harvest Types</span>
        </h1>
        <span className="sm-badge">
          {harvestTypes.length} {harvestTypes.length === 1 ? "Type" : "Types"}
        </span>
      </div>

      <div className="sm-layout">

        {/* ── Form Card ── */}
        <div className="sm-card">
          <div className="sm-card-header">
            <div className="sm-card-icon sm-card-icon--rose">🌾</div>
            <div>
              <p className="sm-card-title">
                {editingItem ? "Edit Harvest Type" : "New Harvest Type"}
              </p>
              <p className="sm-card-sub">
                {editingItem ? `Editing: ${editingItem.name}` : "Define a new harvest category"}
              </p>
            </div>
          </div>

          <div className="sm-card-body">
            {editingItem && (
              <div className="sm-edit-banner" style={{ marginBottom: 16 }}>
                <span>✏️</span>
                <span>Editing <strong>{editingItem.name}</strong></span>
              </div>
            )}

            <form className="sm-form" onSubmit={handleSubmit}>
              <div className="sm-field">
                <label className="sm-label">
                  Harvest Type Name <span className="sm-req">*</span>
                </label>
                <input
                  className="sm-input"
                  name="name"
                  placeholder="e.g. First Grade, Export"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="sm-btn-row">
                <button type="submit" className="sm-btn sm-btn--primary">
                  {editingItem ? "✓ Update Type" : "+ Add Type"}
                </button>
                {editingItem && (
                  <button type="button" className="sm-btn sm-btn--ghost" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* ── Table Card ── */}
        <div className="sm-card">
          <div className="sm-card-header">
            <div className="sm-card-icon sm-card-icon--olive">📋</div>
            <div>
              <p className="sm-card-title">All Harvest Types</p>
              <p className="sm-card-sub">View, edit and manage harvest categories</p>
            </div>
          </div>

          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Harvest Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {harvestTypes.length > 0 ? (
                  harvestTypes.map((item, i) => (
                    <tr key={item.id}>
                      <td><span className="sm-row-index">{i + 1}</span></td>
                      <td><span className="sm-item-name">{item.name}</span></td>
                      <td>
                        <div className="sm-table-actions">
                          <button className="sm-btn sm-btn--edit" onClick={() => handleEdit(item)}>Edit</button>
                          <button className="sm-btn sm-btn--danger" onClick={() => handleDelete(item.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <div className="sm-empty">
                        <div className="sm-empty-icon">🌾</div>
                        <p className="sm-empty-text">No harvest types found</p>
                        <p className="sm-empty-sub">Add your first harvest type using the form</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageHarvestTypes;